/* ============================================================================
   ALIA enquiry widget — single widget, renders once site-wide, bottom right.
   Corporate is the priority action and opens the lead form.

   LAUNCHER: a green circle carrying "?", swapping to "×" when open, glyph in
   near-black. THE CIRCLE STAYS A CIRCLE — every other surface here (panel,
   modal, buttons) is border-radius:0 to match the theme, and a squared
   launcher reads as a different component. Form wells keep a ~2px radius so
   they read as fields rather than flat blocks (§6).

   This replaced an earlier squared "matchbook" cover. Its striker/ticks/strip
   chrome, both @keyframes and the --w-surface-warm token were removed in the
   same edit as the markup and the JS handles (trap 14).

   Shadow-DOM isolated: the theme cannot style it, and it cannot style the theme.
   Custom properties DO inherit across the shadow boundary, so every colour below
   reads the theme's own :root token live (index.liquid ALIA-GREEN-V4), with the
   literal token value as fallback if the widget is ever dropped on another page.

   Tokens used: --espresso --bone --parchment --amber --roast --char
                --esp-rgb --bone-rgb --amber-rgb --char-rgb --ease
                --green --green-deep --green-bright --green-ink

   THIS SITE SELLS THE ROOM AS A PRIVATE EVENT VENUE. No food copy, no service
   hours — `node .dev/verify.js` scans these string literals for both.

   Type:        "Chivo" / "Chivo Mono" — already loaded in templates/index.liquid
   ========================================================================== */
(function () {
  'use strict';

  if (document.getElementById('alia-wgt-host')) return;

  /* ---------------------------------------------------------------- config */
  /* layout/theme.liquid sets window.ALIA_WIDGET_CONFIG.siteName from
     {{ shop.name }} in a NON-deferred tag, so it is always present by the time
     this deferred script runs (§7). */
  var CFG = window.ALIA_WIDGET_CONFIG || {};

  var CONFIG = {
    phone: '+15145662542',
    phoneDisplay: '(514) 566-2542',
    opentable: 'https://www.opentable.ca/r/alia-montreal',

    /* §7 — the shared Google Apps Script Web App. Every ALIA site posts to the
       SAME endpoint so all leads land in one sheet.

       PASTE THE WEB APP URL HERE TO GO LIVE. No other change is needed.

       Deployment settings that will bite you:
         · Access must be "Anyone", NOT "Anyone with a Google account". A
           visitor is anonymous; the stricter setting returns a login page and
           the lead is silently lost.
         · Apps Script serves the LAST DEPLOYED version, not the last saved
           one. Re-deploy after every edit.

       BLIND SPOT: the POST below uses mode:'no-cors' (required — Apps Script
       cannot answer a CORS preflight), so the browser gets an opaque response
       and cannot read the status. The form confirms whenever the request
       LEAVES THE BROWSER, including when the script throws or the deployment
       is stale. Only a total network failure is caught. THE SHEET IS THE ONLY
       PROOF — check Executions after going live and after every redeploy. */
    leadEndpoint: CFG.leadEndpoint || '',

    /* §7 — the Site column must be the store NAME. Falling back to the
       hostname is useless in a sheet shared across stores, but it beats an
       empty column. */
    siteName: CFG.siteName || window.location.hostname
  };

  var DEMO = !CONFIG.leadEndpoint;

  /* The theme's own paper grain, lifted verbatim from .grain so the widget
     sits in the same textured world as the page. */
  var GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

  /* ------------------------------------------------------------------- css */
  var CSS = [
    ':host{',
    /* --- theme tokens, read live from :root, literals as fallback --- */
    '  --w-ink:var(--bone,#EEEDE3);',
    '  --w-ink-rgb:var(--bone-rgb,238,237,227);',
    '  --w-surface:var(--espresso,#584737);',
    '  --w-surface-deep:var(--char,#241A11);',
    '  --w-accent:var(--amber,#8A5F3A);',
    '  --w-accent-rgb:var(--amber-rgb,138,95,58);',
    '  --w-char-rgb:var(--char-rgb,36,26,17);',
    /* §3 role tokens — ink on a green fill is NEAR-BLACK, never white.
       White on #1FA35B is 3.26:1 and fails AA; #08120B is 5.85:1. */
    '  --w-cta:var(--green,#1FA35B);',
    '  --w-cta-bright:var(--green-bright,#27C06D);',
    '  --w-cta-deep:var(--green-deep,#166B41);',
    '  --w-cta-ink:var(--green-ink,#08120B);',
    /* Enquiry-form palette. Every value measured against the ground it sits
       on — see the block comment above the form CSS below. The well is
       LIGHTER than the card: darker reads as a hole, not a place to type. */
    '  --w-night:#100E0A;',
    '  --w-card:#17140F;',
    '  --w-hair:#2A241A;',
    '  --w-cream:#F3EEE1;',
    '  --w-cream-dim:#A79E8C;',
    '  --w-brass:#C9A45C;',
    '  --w-brass-edge:#7A6437;',
    '  --w-brass-lit:#9C8046;',
    '  --w-well:#3D3428;',
    '  --w-well-hover:#4A4031;',
    '  --w-well-focus:#514534;',
    '  --w-hint:#BDB4A2;',
    '  --w-ember:#C4713F;',
    '  --w-ember-lit:#E0705E;',
    /* makes the UA draw a LIGHT date-picker glyph on the dark well */
    '  color-scheme:dark;',
    '  --w-ease:var(--ease,cubic-bezier(.22,.61,.36,1));',
    '  --w-ff:"Chivo",system-ui,sans-serif;',
    '  --w-mono:"Chivo Mono",ui-monospace,monospace;',
    /* the theme's own dock sits at 9400; grain 9000, cursor 9600, toast 9700,
       lightbox 9800 */
    '  position:fixed;inset:0;z-index:9400;pointer-events:none;',
    '  font-family:var(--w-ff);font-weight:300;line-height:1.5;',
    '  -webkit-font-smoothing:antialiased;',
    '}',
    '*{margin:0;padding:0;box-sizing:border-box}',
    'button{font:inherit;color:inherit;background:none;border:none;cursor:pointer}',
    'a{color:inherit;text-decoration:none}',

    /* ---------- shared type utilities, lifted from the theme ---------- */
    '.eyebrow{font-family:var(--w-mono);font-weight:400;font-size:9px;',
    '  letter-spacing:.2em;text-transform:uppercase;white-space:nowrap}',
    '.h{font-weight:300;text-transform:uppercase;letter-spacing:.02em;line-height:.98}',

    /* ---------- pill CTA — .btn from the theme ---------- */
    '.cta{position:relative;display:inline-flex;align-items:center;justify-content:space-between;',
    '  gap:11px;width:100%;overflow:hidden;isolation:isolate;text-align:left;',
    '  font-family:var(--w-mono);font-weight:300;font-size:12.5px;letter-spacing:.2em;',
    '  text-transform:uppercase;padding:15px 26px;border-radius:0;border:1px solid transparent;',
    '  transition:color .5s var(--w-ease),border-color .5s var(--w-ease)}',
    '.cta > *{position:relative;z-index:2}',
    '.cta::before{content:"";position:absolute;inset:0;z-index:1;',
    '  transform:translateY(101%);transition:transform .5s var(--w-ease)}',
    '.cta:hover::before,.cta:focus-visible::before{transform:translateY(0)}',
    '.cta .arw{transition:transform .5s var(--w-ease);flex:none}',
    '.cta:hover .arw{transform:translateX(4px)}',
    '.cta:focus-visible{outline:1px solid var(--w-accent);outline-offset:3px}',
    '.cta[disabled]{opacity:.55;cursor:default}',

    /* Corporate = priority. Green fill, NEAR-BLACK ink, brightening on hover. */
    '.cta.primary{background:var(--w-cta);color:var(--w-cta-ink);border-color:var(--w-cta)}',
    '.cta.primary::before{background:var(--w-cta-bright)}',
    '.cta.primary:hover{border-color:var(--w-cta-bright);color:var(--w-cta-ink)}',

    /* Ghost on the dark card — bone letters (green-deep is 1.8:1 on roast) */
    '.cta.ghost{color:var(--w-ink);border-color:rgba(var(--w-ink-rgb),.3)}',
    '.cta.ghost::before{background:var(--w-cta)}',
    '.cta.ghost:hover{color:var(--w-cta-ink);border-color:var(--w-cta)}',

    /* ---------- the launcher + panel ---------- */
    /* §6: column-reverse so the panel unfolds UPWARD out of the launcher and
       never shoves itself up the screen. align-items:flex-end keeps the circle
       hard against the right edge while the panel is much wider. */
    '.match{position:fixed;right:clamp(.9rem,2.5vw,1.6rem);bottom:clamp(.9rem,2.5vw,1.6rem);',
    '  display:flex;flex-direction:column-reverse;align-items:flex-end;gap:12px;',
    '  pointer-events:none;opacity:0;transform:translateY(14px)}',
    '.match.revealed{opacity:1;transform:none;pointer-events:auto;',
    '  transition:opacity .5s ease,transform .5s ease}',

    /* --- the circle. THE CIRCLE STAYS A CIRCLE: everything else in this
           widget is border-radius:0 to match the theme, and a squared
           launcher reads as a different component entirely. --- */
    '.launcher{position:relative;flex:none;width:58px;height:58px;border-radius:50%;',
    '  display:grid;place-items:center;',
    '  background:var(--w-cta);color:var(--w-cta-ink);',   /* near-black on green, 5.85:1 */
    '  box-shadow:0 10px 28px rgba(var(--w-char-rgb),.42);',
    '  transition:background .35s var(--w-ease),transform .35s var(--w-ease)}',
    '.launcher:hover{background:var(--w-cta-bright);transform:translateY(-2px)}',
    '.launcher:focus-visible{outline:2px solid var(--w-cta-ink);outline-offset:3px}',
    '.launcher .glyph{grid-area:1/1;font-size:25px;line-height:1;font-weight:400;',
    '  transition:opacity .25s var(--w-ease),transform .35s var(--w-ease)}',
    '.launcher .g-open{opacity:1;transform:none}',
    '.launcher .g-close{opacity:0;transform:rotate(-90deg);font-size:22px}',
    '.match.open .launcher .g-open{opacity:0;transform:rotate(90deg)}',
    '.match.open .launcher .g-close{opacity:1;transform:none}',

    /* --- the panel, squared like the rest of the theme --- */
    '.panel{position:relative;width:290px;max-width:calc(100vw - 2rem);',
    '  overflow:hidden;max-height:0;opacity:0;border-radius:0;',
    '  background:var(--w-surface-deep);color:var(--w-ink);',
    '  border:1px solid rgba(var(--w-ink-rgb),.14);',
    '  box-shadow:0 10px 40px rgba(var(--w-char-rgb),.4);',
    '  transition:max-height .45s var(--w-ease),opacity .3s var(--w-ease)}',
    '.match.open .panel{max-height:460px;opacity:1}',
    /* the theme's .hero-glow vocabulary — an amber bloom in the corner */
    '.panel::before{content:"";position:absolute;z-index:0;width:190px;height:190px;',
    '  right:-70px;top:-80px;border-radius:50%;pointer-events:none;',
    '  background:radial-gradient(circle,rgba(var(--w-accent-rgb),.26),rgba(var(--w-accent-rgb),0) 62%)}',
    /* the theme's paper grain */
    '.panel::after{content:"";position:absolute;inset:0;z-index:3;pointer-events:none;',
    '  background-image:' + GRAIN + ';opacity:.13;mix-blend-mode:overlay}',
    '.panel-pad{position:relative;z-index:1;padding:20px 18px 18px}',
    '.panel .eyebrow.head{color:var(--w-accent);letter-spacing:.34em;display:flex;align-items:center}',
    '.panel h2{font-size:22px;margin:11px 0 4px;color:var(--w-ink)}',
    '.panel .lede{font-size:12.5px;line-height:1.6;color:rgba(var(--w-ink-rgb),.62);margin-bottom:15px}',
    /* §6: generous button padding inside the panel */
    '.acts{display:flex;flex-direction:column;gap:10px}',
    '.acts .cta{padding:16px 22px}',
    '.tag{color:var(--w-accent);letter-spacing:.34em;margin-bottom:-1px}',
    '.hair{height:1px;background:rgba(var(--w-ink-rgb),.12);margin:16px 0 12px}',
    '.foot{font-family:var(--w-mono);font-size:9.5px;letter-spacing:.18em;text-transform:uppercase;',
    '  color:rgba(var(--w-ink-rgb),.45);display:flex;justify-content:space-between;gap:10px}',
    '.foot .pipe{color:var(--w-accent)}',

    /* ---------- enquiry form ----------
       Dark card, brass micro-labels, wells LIGHTER than the card behind them.
       Every value below is measured, not picked:

         card        #17140F   the surface the form sits on
         well        #3D3428   the field at rest — LIGHTER than the card.
                               Darker reads as a hole rather than a place to type.
         brass-edge  #7A6437   3.24:1 on the card — the WCAG minimum for a
                               non-text UI boundary. Do not drop it to a hairline.
         brass       #C9A45C   micro-labels, 7.83:1 on the card
         cream       #F3EEE1   values, 10.54:1 on the well
         placeholder #BDB4A2   5.94:1 on the well
         ember       #C4713F   error rail; #E0705E error text, 5.81:1 on card

       The ACTION colour stays the theme's own green, not a separate jade:
       §3 requires the same green in the theme CSS and inside this shadow DOM
       or the launcher stops matching the page buttons. #1FA35B reads 5.64:1
       as a fill on this card, with near-black ink at 5.85:1. */

    /* The UA rule for [hidden] is only display:none, so ANY author rule that
       sets a display value on the same element silently outranks it and the
       element stays on screen. Guard the whole class of bug once. */
    '[hidden]{display:none!important}',

    /* §6: the SCRIM must not scroll — it would stack its own scrollbar next to
       the page's. The card is capped and SHRINKS TO FIT instead. */
    '.scrim{position:fixed;inset:0;z-index:2;display:none;align-items:center;justify-content:center;',
    '  padding:.8rem;pointer-events:auto;overflow:hidden;background:rgba(var(--w-char-rgb),.8);',
    '  -webkit-backdrop-filter:blur(12px);backdrop-filter:blur(12px)}',
    '.scrim.on{display:flex}',

    '.modal{position:relative;width:min(660px,100%);max-height:calc(100dvh - 1.6rem);',
    '  overflow-y:auto;background:var(--w-card);color:var(--w-cream);',
    '  border:1px solid var(--w-hair);border-top:2px solid var(--w-cta);',
    '  border-radius:0;', /* sharp — every rectangle on this site is squared */
    '  box-shadow:0 24px 70px rgba(0,0,0,.55);animation:w-rise .45s var(--w-ease)}',
    '@keyframes w-rise{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}',
    '.modal :focus-visible{outline:2px solid var(--w-cta-bright);outline-offset:3px}',

    /* --- header --- */
    '.m-head{padding:var(--m-hpad,26px) var(--m-x,28px) var(--m-hb,20px);border-bottom:1px solid var(--w-hair);position:relative}',
    '.modal .eyebrow{color:var(--w-brass);letter-spacing:.28em;font-size:11px;margin:0}',
    '.modal h2{font-size:var(--m-title,1.6rem);font-weight:300;letter-spacing:-.02em;',
    '  margin:6px 0;line-height:1.15;text-transform:none;color:var(--w-cream)}',
    '.modal .lede{margin:0;font-size:14px;color:var(--w-cream-dim);max-width:46ch;line-height:1.6}',
    '.m-close{position:absolute;top:18px;right:18px;width:34px;height:34px;border-radius:0;',
    '  display:grid;place-items:center;font-size:15px;line-height:1;',
    '  color:var(--w-cream-dim);border:1px solid var(--w-hair);',
    '  transition:color .25s,background .25s,border-color .25s}',
    '.m-close:hover{color:var(--w-cream);background:var(--w-well);border-color:var(--w-brass-edge)}',
    '.m-body{padding:var(--m-bpad,24px) var(--m-x,28px) var(--m-bb,28px)}',

    /* --- field grid --- */
    '.ae-grid{display:grid;grid-template-columns:1fr 1fr;gap:var(--m-gap,15px)}',
    '.ae-span2{grid-column:1/-1}',
    /* Two columns are kept all the way down. Collapsing to one stacks nine
       fields into 1122px and guarantees a scroll on every phone; two columns
       plus a full-width email comes to ~692px and fits. Only below 340px,
       where a half column stops being usable at all, does it go single. */
    '@media(max-width:340px){.ae-grid{grid-template-columns:1fr}}',
    '.field.wide-sm{grid-column:auto}',
    '@media(max-width:620px){',
    '  .modal{--m-x:16px;--m-hpad:16px;--m-hb:14px;--m-bpad:16px;--m-bb:20px;',
    '         --m-ta:64px;--m-title:1.3rem;--m-fpad:11px;--m-gap:12px;--m-g2:6px;',
    '         --m-fm:16px;--m-fp:14px}',
    '  .field.wide-sm{grid-column:1/-1}',
    '  .modal .m-head .lede{display:none}',
    '  .m-foot{gap:10px}',
    '  .m-foot .btn-send{width:100%;justify-content:center}',
    '}',

    '.field{display:flex;flex-direction:column;gap:var(--m-g2,7px);min-width:0;position:relative}',
    '.field.hide{display:none}',
    'label{font-family:var(--w-mono);font-weight:400;font-size:10.5px;letter-spacing:.2em;',
    '  text-transform:uppercase;color:var(--w-brass);display:block}',
    '.opt{color:var(--w-brass-edge)}',

    /* --- the well, at rest ---
       The rim is an INSET box-shadow rather than a border so the state rails
       below can swap the whole stack without changing the box model. */
    'input,select,textarea{font-family:var(--w-ff);font-weight:300;',
    '  font-size:16px;', /* §6: below 16px iOS zooms the page on focus */
    '  color:var(--w-cream);background:var(--w-well);border:0;border-radius:2px;',
    '  padding:var(--m-fpad,13px) 14px;width:100%;',
    '  box-shadow:inset 0 0 0 1px var(--w-brass-edge),inset 0 1px 0 var(--w-brass-lit),',
    '             0 1px 0 rgba(0,0,0,.5),0 4px 12px -6px rgba(0,0,0,.7);',
    '  transition:background .16s ease,box-shadow .16s ease}',
    'textarea{resize:vertical;min-height:var(--m-ta,88px);font-size:15px;line-height:1.55}',
    'input::placeholder,textarea::placeholder{color:var(--w-hint);opacity:1}',

    'input:hover,select:hover,textarea:hover{background:var(--w-well-hover);',
    '  box-shadow:inset 0 0 0 1px var(--w-brass-lit),inset 0 1px 0 #B08F4E,',
    '             0 1px 0 rgba(0,0,0,.5),0 4px 12px -6px rgba(0,0,0,.7)}',

    'input:focus,select:focus,textarea:focus{outline:none;background:var(--w-well-focus);',
    '  box-shadow:inset 0 0 0 2px var(--w-cta-bright),0 0 0 4px rgba(31,163,91,.16)}',

    /* --- FILLED ---
       §6: STATE LIVES ON THE .field WRAPPER, never on pseudo-classes on the
       control, so every rule sits at one specificity tier and source order IS
       priority order. That is what stops "filled" outranking "invalid".
       The :not() matters: without it a malformed email that is also focused
       wears the success rail while wrong. */
    '.field.is-filled:not(.is-invalid) input,',
    '.field.is-filled:not(.is-invalid) select,',
    '.field.is-filled:not(.is-invalid) textarea{',
    '  box-shadow:inset 4px 0 0 var(--w-cta),inset 0 0 0 1px var(--w-brass-edge),',
    '             inset 0 1px 0 var(--w-brass-lit),0 1px 0 rgba(0,0,0,.5)}',
    '.field.is-filled:not(.is-invalid) input:focus,',
    '.field.is-filled:not(.is-invalid) select:focus,',
    '.field.is-filled:not(.is-invalid) textarea:focus{',
    '  box-shadow:inset 4px 0 0 var(--w-cta),inset 0 0 0 2px var(--w-cta-bright),',
    '             0 0 0 4px rgba(31,163,91,.16)}',

    /* --- INVALID: full rim + reddened label, findable from across the form --- */
    '.field.is-invalid input,.field.is-invalid select,.field.is-invalid textarea{',
    '  box-shadow:inset 4px 0 0 var(--w-ember),inset 0 0 0 1px #B4523C,0 1px 0 rgba(0,0,0,.5)}',
    '.field.is-invalid input:focus,.field.is-invalid select:focus,.field.is-invalid textarea:focus{',
    '  box-shadow:inset 4px 0 0 var(--w-ember),inset 0 0 0 2px var(--w-ember),',
    '             0 0 0 4px rgba(196,113,63,.18)}',
    '.field.is-invalid label{color:var(--w-ember-lit)}',
    '.err{display:none;font-family:var(--w-mono);font-size:10.5px;letter-spacing:.1em;',
    '  text-transform:uppercase;color:var(--w-ember-lit)}',
    '.field.is-invalid .err{display:block}',

    /* --- native control quirks ---
       An EMPTY <input type="date"> paints its yyyy-mm-dd hint at FULL text
       colour, so the one blank required field reads as the most complete on
       the form. Dim it until it actually holds a value. */
    '.field:not(.is-filled) input[type="date"]{color:var(--w-hint)}',
    'input[type="number"]{-moz-appearance:textfield;appearance:textfield}',
    'input[type="number"]::-webkit-outer-spin-button,',
    'input[type="number"]::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}',
    'select{appearance:none;-webkit-appearance:none;cursor:pointer;padding-right:34px}',
    '.select-wrap{position:relative}',
    '.select-wrap::after{content:"\\25BE";position:absolute;right:13px;bottom:12px;',
    '  color:var(--w-brass);pointer-events:none;font-size:12px}',
    /* Chrome paints autofill with its own background unless a large inset
       shadow covers it, and box-shadow paints the FIRST layer on top, so the
       covering fill has to be the LAST layer. */
    'input:-webkit-autofill{-webkit-text-fill-color:var(--w-cream);',
    '  transition:background-color 8000s ease-in-out 0s;',
    '  -webkit-box-shadow:inset 0 0 0 1px var(--w-brass-edge),inset 0 1px 0 var(--w-brass-lit),',
    '                     inset 0 0 0 100px var(--w-well)}',

    /* --- footer --- */
    '.m-foot{display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap;',
    '  margin-top:var(--m-fm,22px);padding-top:var(--m-fp,20px);border-top:1px solid var(--w-hair)}',
    '.m-foot .fine{margin:0;font-size:12.5px;color:var(--w-cream-dim);max-width:34ch}',
    '.btn-send{font-family:var(--w-mono);font-size:11.5px;letter-spacing:.16em;text-transform:uppercase;',
    '  white-space:nowrap;border-radius:0;padding:14px 20px;cursor:pointer;display:inline-flex;',
    '  align-items:center;gap:8px;border:1px solid var(--w-cta);background:var(--w-cta);',
    '  color:var(--w-cta-ink);font-weight:700;',
    '  transition:background .16s ease,box-shadow .2s ease,transform .16s ease}',
    '.btn-send:hover{background:var(--w-cta-bright);box-shadow:0 6px 26px -8px rgba(31,163,91,.8)}',
    '.btn-send:active{transform:translateY(1px)}',
    '.btn-send[disabled]{opacity:.6;cursor:progress}',
    '.btn-ghost{background:transparent;color:var(--w-cta-bright);border-color:#1D5F3D;font-weight:400}',
    '.btn-ghost:hover{background:rgba(31,163,91,.12);border-color:var(--w-cta);box-shadow:none}',

    /* ---------- confirmation, replacing the form IN PLACE ----------
       Sized to be read in ONE VIEW — it must not scroll. The previous layout
       stacked the check mark above the heading and listed all seven summary
       values in a single column: 852px, which overflowed every laptop.
       Two changes carry it — the mark sits BESIDE the heading, and the summary
       runs in two columns so seven items occupy four rows. 585px at rest. */
    '.done{display:flex;flex-direction:column;gap:var(--d-gap,13px);padding:4px 0 2px}',
    '.done-top{display:flex;gap:14px;align-items:flex-start}',
    '.done-mark{flex:none;width:var(--d-mark,40px);height:var(--d-mark,40px);border-radius:50%;',
    '  background:var(--w-cta);color:var(--w-cta-ink);display:grid;place-items:center;',
    '  font-size:20px;font-weight:700;line-height:1}',
    '.done-h{font-size:var(--d-h,1.5rem);font-weight:300;letter-spacing:-.02em;margin:0 0 8px;line-height:1.1}',
    '.done-lede{margin:0;color:var(--w-cream-dim);font-size:14px;max-width:48ch;line-height:1.55}',
    '.done-lede b{color:var(--w-cream);font-weight:600}',
    /* Two columns, so seven values are four rows rather than seven. The 1px grid
       gap over a hair-coloured ground is what draws the rules between cells. */
    '.summary{display:grid;grid-template-columns:1fr;gap:1px;background:var(--w-hair);',
    '  border:1px solid var(--w-hair);border-radius:0;overflow:hidden;margin:0}',
    '@media(min-width:560px){.summary{grid-template-columns:1fr 1fr}}',
    /* An odd final item would leave a hair-coloured hole in the last row, so it
       spans the full width instead. Correct for any count, odd or even. */
    '.summary > div:last-child:nth-child(odd){grid-column:1/-1}',
    '.summary > div{background:var(--w-night);padding:var(--d-row,8px) 14px;display:flex;',
    '  justify-content:space-between;align-items:baseline;gap:14px;font-size:13px;min-width:0}',
    '.summary dt{font-family:var(--w-mono);font-size:10px;letter-spacing:.18em;',
    '  text-transform:uppercase;color:var(--w-brass);flex:none}',
    '.summary dd{margin:0;text-align:right;color:var(--w-cream);min-width:0;overflow-wrap:anywhere}',
    '.done-next{border-top:1px solid var(--w-hair);padding-top:var(--d-np,14px);display:flex;',
    '  flex-direction:column;gap:10px}',
    '.done-actions{display:flex;gap:10px;flex-wrap:wrap}',
    '.done-next .btn-send{padding:11px 16px}',
    '.done-fine{margin:0;font-size:12px;color:var(--w-brass-edge);max-width:52ch;line-height:1.5}',


    /* ---------- modal height budget (§6/§9.5) ----------
       Computed, not guessed. Shrink order is padding, then the textarea, then
       supporting copy. FIELDS AND THE SUBMIT BUTTON NEVER GO, and the scrim
       never scrolls. Heights and the viewport each tier needs:
         base    756px  needs >=783
         <=820   710px  needs >=736
         <=760   610px  needs >=636
         <=700   564px  needs >=590
         <=640   512px  needs >=538
       Verified fitting at 1000/900/860/820/800/780/760/740/700/680/660/640/
       620/600/560. overflow-y:auto stays as a backstop below that. */
    "@media(max-height:820px){",
    "  .modal{--m-hpad:18px;--m-hb:16px;--m-bpad:18px;--m-bb:22px;--m-ta:72px;--m-title:1.45rem;--m-fm:20px;--m-fp:18px}",
    "}",
    "@media(max-height:760px){",
    "  .modal{--m-hpad:14px;--m-hb:14px;--m-bpad:14px;--m-bb:20px;--m-ta:56px;--m-title:1.3rem;",
    "         --m-fpad:12px;--m-gap:13px;--m-g2:6px;--m-fm:18px;--m-fp:16px}",
    "  .modal .m-head .lede{display:none}",
    "}",
    "@media(max-height:700px){",
    "  .modal{--m-hpad:11px;--m-hb:10px;--m-bpad:11px;--m-bb:12px;--m-ta:42px;--m-title:1.15rem;",
    "         --m-fpad:9px;--m-gap:10px;--m-g2:5px;--m-fm:12px;--m-fp:11px}",
    "  .modal .m-foot .fine{display:none}",
    "  .modal .m-head .lede{display:none}",
    "}",
    /* The confirmation is shorter than the form (585px vs 756px), so it only
       needs help on the very shortest screens. */
    "@media(max-height:660px){",
    "  .done{--d-gap:10px;--d-mark:34px;--d-h:1.3rem;--d-row:6px;--d-np:11px}",
    "  .done-lede{font-size:13px}",
    "}",
    "@media(max-height:640px){",
    "  .modal{--m-hpad:8px;--m-hb:8px;--m-bpad:8px;--m-bb:10px;--m-ta:36px;--m-title:1rem;",
    "         --m-fpad:7px;--m-gap:8px;--m-g2:4px;--m-fm:10px;--m-fp:9px}",
    "  .modal .m-head .eyebrow{display:none}",
    "}",

    /* ---------- toast ---------- */
    '.toast{position:fixed;left:50%;bottom:34px;z-index:3;opacity:0;pointer-events:none;',
    '  transform:translate(-50%,20px);background:var(--w-surface);color:var(--w-ink);',
    '  font-family:var(--w-mono);font-size:12px;letter-spacing:.18em;text-transform:uppercase;',
    '  padding:13px 22px;border-radius:0;box-shadow:0 10px 40px rgba(var(--w-char-rgb),.4);',
    '  transition:opacity .4s var(--w-ease),transform .4s var(--w-ease);max-width:calc(100vw - 32px)}',
    '.toast.on{opacity:1;transform:translate(-50%,0)}',
    '.toast .em{color:var(--w-accent)}',

    /* ---------- ≤600px: mirror the theme dock's full-width behaviour ---------- */
    /* §6: below 600px the panel is clamped to calc(100vw - 2rem) (set on
       .panel above) or it overflows a phone. The launcher stays a 58px circle
       anchored right — it must not stretch. */
    '@media(max-width:600px){',
    '  .match{right:12px;bottom:12px}',
    '  .panel .lede{display:none}',
    '}',

    '@media(prefers-reduced-motion:reduce){',
    '  .match,.match.revealed,.launcher,.launcher .glyph,.panel,.cta,.cta::before,.toast{',
    '    transition-duration:.01ms!important}',
    '  .modal{animation:none}',
    '}'
  ].join('\n');

  /* ------------------------------------------------------------------ html */
  /* §6: nine fields, every one with a placeholder, plus the conditional Event
     detail shown only when Event = "Something else".
     The placeholder <option> carries value="" — without it an untouched select
     reports as an answer. */
  var HTML =
    '<div class="match" data-match>' +
      '<button class="launcher" data-toggle aria-expanded="false" aria-controls="alia-card" aria-label="Open enquiry panel">' +
        '<span class="glyph g-open" aria-hidden="true">?</span>' +
        '<span class="glyph g-close" aria-hidden="true">&#10005;</span>' +
      '</button>' +
      '<div class="panel" id="alia-card" data-card>' +
        '<div class="panel-pad">' +
          '<p class="eyebrow head">Private events &middot; Corporate</p>' +
          '<h2 class="h">Plan a day</h2>' +
          '<p class="lede">350 to 400 inside, 150 to 170 outside. One vendor, one contact.</p>' +
          '<div class="acts">' +
            '<p class="eyebrow tag">Priority</p>' +
            '<button class="cta primary" data-act="corp">' +
              '<span>Corporate events</span>' +
              '<span class="arw" aria-hidden="true">&rarr;</span>' +
            '</button>' +
            '<a class="cta ghost" data-act="rest" href="' + CONFIG.opentable + '" target="_blank" rel="noopener">' +
              '<span>Restaurant &middot; Book</span><span class="arw" aria-hidden="true">&#8599;</span>' +
            '</a>' +
            '<a class="cta ghost" data-act="club" href="tel:' + CONFIG.phone + '">' +
              '<span>Club &middot; Call</span><span class="arw" aria-hidden="true">&rarr;</span>' +
            '</a>' +
          '</div>' +
          '<div class="hair" aria-hidden="true"></div>' +
          '<p class="foot"><span>One vendor<span class="pipe"> &middot; </span>One contact</span>' +
            '<span>' + CONFIG.phoneDisplay + '</span></p>' +
        '</div>' +
      '</div>' +
    '</div>' +

    '<div class="scrim" data-scrim>' +
      '<form class="modal" data-modal role="dialog" aria-modal="true" aria-labelledby="alia-mtitle" novalidate>' +

        '<div class="m-head">' +
          '<button type="button" class="m-close" data-mclose aria-label="Close">&#10005;</button>' +
          '<p class="eyebrow">Private events</p>' +
          '<h2 id="alia-mtitle" data-mtitle>Request a date</h2>' +
          '<p class="lede" data-msub>We answer the same day, with availability and a walk-through of the room.</p>' +
        '</div>' +

        '<div class="m-body">' +
        '<div class="form-view">' +
          '<div class="ae-grid">' +

            '<div class="field" data-field><label for="alia-name">Your name</label>' +
              '<input id="alia-name" name="name" type="text" autocomplete="name" placeholder="Alex Tremblay" required>' +
              '<span class="err">Required</span></div>' +

            '<div class="field" data-field><label for="alia-co">Company</label>' +
              '<input id="alia-co" name="company" type="text" autocomplete="organization" placeholder="Northbound Capital" required>' +
              '<span class="err">Required</span></div>' +

            '<div class="field wide-sm" data-field><label for="alia-email">Work email</label>' +
              '<input id="alia-email" name="email" type="email" autocomplete="email" placeholder="alex@northbound.ca" required>' +
              '<span class="err">Enter a valid email</span></div>' +

            '<div class="field" data-field><label for="alia-phone">Phone</label>' +
              '<input id="alia-phone" name="phone" type="tel" autocomplete="tel" placeholder="514 555 0142" required>' +
              '<span class="err">Required</span></div>' +

            '<div class="field" data-field><label for="alia-type">Event</label>' +
              '<div class="select-wrap">' +
                /* the placeholder option MUST carry value="" or an untouched
                   select reports as an answer */
                '<select id="alia-type" name="event_type" required>' +
                  '<option value="" selected>Choose one</option>' +
                  '<option>Conference</option>' +
                  '<option>Product launch</option>' +
                  '<option>Client day</option>' +
                  '<option>Full buyout</option>' +
                  '<option>Reception</option>' +
                  '<option>Something else</option>' +
                '</select>' +
              '</div>' +
              '<span class="err">Required</span></div>' +

            '<div class="field" data-field><label for="alia-guests">Guests</label>' +
              '<input id="alia-guests" name="guests" type="number" min="1" max="400" inputmode="numeric" placeholder="60" required>' +
              '<span class="err">Required</span></div>' +

            '<div class="field" data-field><label for="alia-date">Preferred date</label>' +
              '<input id="alia-date" name="date" type="date" required>' +
              '<span class="err">Required</span></div>' +

            '<div class="field" data-field><label for="alia-budget">Budget per head <span class="opt">(optional)</span></label>' +
              '<div class="select-wrap">' +
                '<select id="alia-budget" name="budget">' +
                  '<option value="" selected>Not sure yet</option>' +
                  '<option>$95 to $150</option>' +
                  '<option>$150 to $225</option>' +
                  '<option>$225 and up</option>' +
                '</select>' +
              '</div></div>' +

            /* TRAP 12: a hidden required field still blocks validation with
               nothing on screen to explain why — `required` is toggled with
               visibility, never left on. */
            '<div class="field ae-span2 hide" data-field data-conditional>' +
              '<label for="alia-detail">Event detail</label>' +
              '<input id="alia-detail" name="event_detail" type="text" placeholder="Tell us what it is">' +
              '<span class="err">Required</span></div>' +

            '<div class="field ae-span2" data-field>' +
              '<label for="alia-notes">Anything we should know <span class="opt">(optional)</span></label>' +
              '<textarea id="alia-notes" name="notes" placeholder="Run of day, AV, a speech, a hard end time."></textarea></div>' +

          '</div>' +

          '<div class="m-foot">' +
            '<p class="fine">' +
              (DEMO ? 'Demo mode &middot; no lead is sent'
                    : 'We hold provisional dates for 48 hours, no deposit.') +
            '</p>' +
            '<button type="submit" class="btn-send" data-msubmit>Send request</button>' +
          '</div>' +
        '</div>' +

        '<div class="done" data-done hidden aria-live="polite">' +
          '<div class="done-top">' +
            '<div class="done-mark" aria-hidden="true">&#10003;</div>' +
            '<div>' +
              '<h3 class="done-h" data-done-title>Thank you.</h3>' +
              '<p class="done-lede" data-done-lede></p>' +
            '</div>' +
          '</div>' +
          '<dl class="summary" data-done-sum></dl>' +
          '<div class="done-next">' +
            '<p class="eyebrow">If you need us sooner</p>' +
            '<div class="done-actions">' +
              '<a class="btn-send btn-ghost" href="tel:' + CONFIG.phone + '">Call the events line</a>' +
            '</div>' +
            '<p class="done-fine" data-done-fine></p>' +
          '</div>' +
        '</div>' +
        '</div>' +

      '</form>' +
    '</div>' +

    '<div class="toast" data-toast role="status" aria-live="polite"></div>';

  /* ------------------------------------------------------------------ mount */
  var host = document.createElement('div');
  host.id = 'alia-wgt-host';
  document.body.appendChild(host);
  var root = host.attachShadow({ mode: 'open' });
  root.innerHTML = '<style>' + CSS + '</style>' + HTML;

  /* The widget supersedes the theme's own Reserve/Call dock — hide it so the
     two don't stack in the same bottom-right corner. */
  var hide = document.createElement('style');
  hide.textContent = '.alia-cta-dock{display:none!important}';
  document.head.appendChild(hide);

  var match = root.querySelector('[data-match]');
  var launcher = root.querySelector('[data-toggle]');
  var card = root.querySelector('[data-card]');
  var scrim = root.querySelector('[data-scrim]');
  var modal = root.querySelector('[data-modal]');
  var toast = root.querySelector('[data-toast]');
  var conditional = root.querySelector('[data-conditional]');
  var done = root.querySelector('[data-done]');
  /* the sub-heading is restored verbatim after a reset */
  var MSUB = root.querySelector('[data-msub]').textContent;
  var typeSel = root.querySelector('#alia-type');
  var detailInput = root.querySelector('#alia-detail');

  /* ------------------------------------------------------------------ toast */
  var tTimer;
  function showToast(html) {
    toast.innerHTML = html;              /* literal strings only — never user input */
    toast.classList.add('on');
    clearTimeout(tTimer);
    tTimer = setTimeout(function () { toast.classList.remove('on'); }, 3200);
  }

  /* --------------------------------------------------------- open / collapse */
  function setOpen(open) {
    match.classList.toggle('open', open);
    launcher.setAttribute('aria-expanded', open ? 'true' : 'false');
    /* the glyph swaps ? -> x in CSS; the accessible name has to swap too */
    launcher.setAttribute('aria-label', open ? 'Close enquiry panel' : 'Open enquiry panel');
    card.setAttribute('aria-hidden', open ? 'false' : 'true');
  }
  setOpen(false);

  launcher.addEventListener('click', function () {
    setOpen(launcher.getAttribute('aria-expanded') !== 'true');
  });

  document.addEventListener('click', function (e) {
    if (!match.classList.contains('open')) return;
    if (scrim.classList.contains('on')) return;
    if (e.composedPath().indexOf(host) !== -1) return;
    setOpen(false);
  });

  /* ---------------------------------------------------------- page locking */
  /* §6: lock the page while the modal is open and compensate padding-right
     with the scrollbar width, or the page jumps sideways as it locks. */
  var savedOverflow = '', savedPad = '';
  function lockPage() {
    var sw = window.innerWidth - document.documentElement.clientWidth;
    savedOverflow = document.body.style.overflow;
    savedPad = document.body.style.paddingRight;
    document.body.style.overflow = 'hidden';
    if (sw > 0) document.body.style.paddingRight = sw + 'px';
    if (window.__aliaLenis && window.__aliaLenis.stop) { try { window.__aliaLenis.stop(); } catch (e) {} }
  }
  function unlockPage() {
    document.body.style.overflow = savedOverflow;
    document.body.style.paddingRight = savedPad;
    if (window.__aliaLenis && window.__aliaLenis.start) { try { window.__aliaLenis.start(); } catch (e) {} }
  }

  /* ------------------------------------------------------------- lead form */
  var lastFocus = null;

  function openModal() {
    lastFocus = document.activeElement;
    scrim.classList.add('on');
    lockPage();
    var first = modal.querySelector('.form-view input:not([type=hidden])');
    if (first) first.focus();
  }
  function closeModal() {
    scrim.classList.remove('on');
    unlockPage();
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  root.querySelector('[data-mclose]').addEventListener('click', closeModal);
  scrim.addEventListener('click', function (e) { if (e.target === scrim) closeModal(); });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (scrim.classList.contains('on')) closeModal();
    else if (match.classList.contains('open')) setOpen(false);
  });

  modal.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var f = [].slice.call(modal.querySelectorAll('button,input,select,textarea,a[href]'))
      .filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && root.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && root.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* Corporate is the only action that needs JS — the other two are real links */
  root.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('[data-act="corp"]')) openModal();
  });

  /* The page's own primary buttons carry data-alia-open="corp" with a tel:
     href as the no-JS fallback (§5). They live in the light DOM, so they need
     a document-level listener. */
  document.addEventListener('click', function (e) {
    var trigger = e.target && e.target.closest && e.target.closest('[data-alia-open="corp"]');
    if (!trigger) return;
    e.preventDefault();
    setOpen(false);
    openModal();
  });

  /* ----------------------------------------------------- conditional field */
  /* TRAP 12 again: `required` is added and removed WITH visibility. */
  function syncConditional() {
    var show = typeSel.value === 'Something else';
    conditional.classList.toggle('hide', !show);
    if (show) detailInput.setAttribute('required', 'required');
    else { detailInput.removeAttribute('required'); clearState(conditional); detailInput.value = ''; }
  }
  typeSel.addEventListener('change', syncConditional);
  syncConditional();

  /* a date in the past helps nobody. Built from LOCAL parts, not
     toISOString(), which shifts to UTC and can offer yesterday (trap 10). */
  (function () {
    var d = new Date();
    var p = function (n) { return (n < 10 ? '0' : '') + n; };
    var el = root.querySelector('#alia-date');
    if (el) el.min = d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
  })();

  /* ------------------------------------------------------------ field state */
  function fieldOf(el) { return el.closest('[data-field]'); }
  function clearState(f) { f.classList.remove('is-filled', 'is-invalid'); }

  function markFilled(el) {
    var f = fieldOf(el);
    if (!f) return;
    f.classList.toggle('is-filled', String(el.value || '').trim() !== '');
  }
  [].forEach.call(modal.querySelectorAll('input,select,textarea'), function (el) {
    el.addEventListener('input', function () { markFilled(el); fieldOf(el).classList.remove('is-invalid'); });
    el.addEventListener('change', function () { markFilled(el); });
  });

  function emailOK(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v || '').trim()); }

  function validate() {
    var ok = true;
    [].forEach.call(modal.querySelectorAll('[data-field]'), function (f) { f.classList.remove('is-invalid'); });

    function fail(el) { fieldOf(el).classList.add('is-invalid'); ok = false; }

    /* Driven by the required ATTRIBUTE rather than a hand-kept list, so adding
       a field to the markup cannot silently go unvalidated. A field inside a
       .hide wrapper is skipped: TRAP 12 is a hidden required control blocking
       submission with nothing on screen to explain why. */
    [].forEach.call(modal.querySelectorAll('.form-view [required]'), function (el) {
      var f = fieldOf(el);
      if (!f || f.classList.contains('hide')) return;
      if (!String(el.value || '').trim()) fail(el);
    });

    var email = root.querySelector('#alia-email');
    if (String(email.value || '').trim() && !emailOK(email.value)) fail(email);

    if (!ok) {
      var bad = modal.querySelector('.is-invalid input,.is-invalid select,.is-invalid textarea');
      if (bad) bad.focus({ preventScroll: true });
    }
    return ok;
  }

  /* TRAP 10: new Date('2026-08-27') parses as UTC and renders as the PREVIOUS
     day west of Greenwich. Split the ISO string and construct locally. */
  function prettyDate(iso) {
    var p = String(iso || '').split('-');
    if (p.length !== 3) return '';
    var d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
    if (isNaN(d.getTime())) return '';
    try {
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (e) { return iso; }
  }

  /* ------------------------------------------------------- confirmation view */
  function showConfirmation(v) {
    var first = String(v.name || '').trim().split(/\s+/)[0] || 'there';

    /* TRAP 13: everything below came from a stranger's keyboard —
       textContent, never innerHTML. */
    root.querySelector('[data-done-title]').textContent = 'Thank you, ' + first;

    var bits = [];
    if (v.guests) bits.push(v.guests + ' guests');
    if (v.date) bits.push('on ' + prettyDate(v.date));
    root.querySelector('[data-done-lede]').textContent = bits.length
      ? 'We have your enquiry for ' + bits.join(' ') + '. The planner will come back to you.'
      : 'We have your enquiry. The planner will come back to you.';

    var sum = root.querySelector('[data-done-sum]');
    sum.textContent = '';
    [
      ['Event', v.event_type === 'Something else' ? (v.event_detail || v.event_type) : v.event_type],
      ['Guests', v.guests],
      ['Date', prettyDate(v.date)],
      ['Company', v.company],
      ['Email', v.email],
      ['Phone', v.phone],
      ['Budget', v.budget]
    ].forEach(function (pair) {
      if (!pair[1]) return;
      /* each row is its own grid cell so the 1px gap stripes them */
      var row = document.createElement('div');
      var dt = document.createElement('dt'); dt.textContent = pair[0];
      var dd = document.createElement('dd'); dd.textContent = pair[1];
      row.appendChild(dt); row.appendChild(dd);
      sum.appendChild(row);
    });

    /* the header restates the outcome — the form title is no longer true */
    root.querySelector('[data-mtitle]').textContent = 'Request received';
    root.querySelector('[data-msub]').textContent = 'One of the events team is already on it.';

    root.querySelector('[data-done-fine]').textContent = DEMO
      ? 'Demo mode: nothing was sent. Paste the Apps Script URL into layout/theme.liquid to go live.'
      : 'The planner comes back to you. If anything is urgent, call the events line above.';

    /* [hidden] is the switch, and the CSS carries [hidden]{display:none!important}
       because the UA rule is only display:none and ANY author display value on
       the same element silently outranks it. */
    root.querySelector('.form-view').hidden = true;
    done.hidden = false;
    modal.scrollTop = 0;
  }

  /* TRAP 11: form.reset() clears values but NOT the wrapper state classes —
     without this the next open shows success rings on empty fields. */
  function resetForm() {
    modal.reset();
    [].forEach.call(modal.querySelectorAll('[data-field]'), clearState);
    root.querySelector('.form-view').hidden = false;
    done.hidden = true;
    root.querySelector('[data-mtitle]').textContent = 'Request a date';
    root.querySelector('[data-msub]').textContent = MSUB;
    syncConditional();
  }

  modal.addEventListener('submit', function (e) {
    e.preventDefault();
    if (!validate()) return;

    var v = {};
    new FormData(modal).forEach(function (val, k) { v[k] = String(val).trim(); });

    /* §7 — THE PAYLOAD CONTRACT. The receiving script builds each row by
       looking up the SHEET'S HEADER TEXT in the posted JSON, so every value
       goes under BOTH its header name and a snake_case key. Sending both costs
       nothing and makes the widget portable across sites whose scripts differ.

       Symptom of getting this wrong: rows appear with only the server-set
       timestamp and every other column blank — the script ran, recognised
       nothing, and returned ok. */
    var payload = {};
    function put(header, snake, value) {
      payload[header] = value === undefined || value === null ? '' : value;
      payload[snake] = payload[header];
    }
    put('Name', 'name', v.name);
    put('Company', 'company', v.company);
    put('Email', 'email', v.email);
    put('Phone', 'phone', v.phone);
    put('Event type', 'event_type', v.event_type);
    put('Event detail', 'event_detail', v.event_detail);
    put('Guests', 'guests', v.guests);
    put('Preferred date', 'date', v.date);
    put('Budget per head', 'budget', v.budget);
    put('Notes', 'notes', v.notes);
    put('Site', 'site', CONFIG.siteName);     /* the STORE NAME, not a hostname */
    put('Page', 'page', window.location.href);
    put('Status', 'status', 'New');

    var btn = root.querySelector('[data-msubmit]');
    btn.disabled = true;
    btn.textContent = 'Sending';

    if (DEMO) {
      /* No endpoint wired yet — log the exact payload so the contract can be
         verified locally before the Apps Script URL is pasted in. */
      if (window.console && console.log) console.log('[ALIA widget] demo lead', payload);
      btn.disabled = false;
      btn.textContent = 'Send request';
      btn.textContent = 'Send request';
      showConfirmation(v);
      showToast('Demo <span class="em">&middot;</span> Lead not sent');
      return;
    }

    fetch(CONFIG.leadEndpoint, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload)
    }).then(function () {
      btn.disabled = false;
      btn.textContent = 'Send request';
      showConfirmation(v);
      showToast('Sent <span class="em">&middot;</span> We will be in touch');
    }).catch(function () {
      btn.disabled = false;
      btn.textContent = 'Send request';
      showToast('Could not send <span class="em">&middot;</span> Call ' + CONFIG.phoneDisplay);
    });
  });

  /* A fresh form next time the launcher is used, but not while the visitor is
     still reading their confirmation. */
  root.querySelector('[data-mclose]').addEventListener('click', function () {
    if (!done.hidden) resetForm();
  });
  scrim.addEventListener('click', function (e) {
    if (e.target === scrim && !done.hidden) resetForm();
  });

  /* --------------------------------------------- reveal after the preloader */
  /* The theme's preloader is .pre and gets .done when it finishes. Match the
     dock's timing so the widget never sits over the intro, with a hard 6s
     ceiling in case the preloader is removed. */
  function reveal() { match.classList.add('revealed'); }

  function watchPreloader() {
    var pre = document.querySelector('.pre');
    if (!pre) {
      if (document.readyState === 'complete') setTimeout(reveal, 700);
      else window.addEventListener('load', function () { setTimeout(reveal, 700); });
      return;
    }
    var start = Date.now();
    var iv = setInterval(function () {
      var el = document.querySelector('.pre');
      var gone = !el;
      if (el) {
        var cs = getComputedStyle(el);
        gone = el.classList.contains('done') || cs.display === 'none' ||
               cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.1;
      }
      if (gone || Date.now() - start > 6000) { reveal(); clearInterval(iv); }
    }, 200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchPreloader);
  } else {
    watchPreloader();
  }
})();
